import { create } from 'zustand';
import { calendarService, CalendarEvent } from '@/services/calendarService';

interface CalendarState {
  events: CalendarEvent[];
  loading: boolean;
  error: string | null;
  view: 'month' | 'week' | 'day' | 'list';
  currentDate: Date;
  selectedDate: Date | null;
  
  // Actions
  fetchEvents: (startDate: string, endDate: string, clientId?: string, practiceId?: string, userId?: string) => Promise<void>;
  setView: (view: CalendarState['view']) => void;
  setCurrentDate: (date: Date) => void;
  setSelectedDate: (date: Date | null) => void;
  reset: () => void;
}

const initialState = {
  events: [],
  loading: false,
  error: null,
  view: 'month' as const,
  currentDate: new Date(),
  selectedDate: null,
};

export const useCalendarStore = create<CalendarState>((set, get) => ({
  ...initialState,

  fetchEvents: async (startDate: string, endDate: string, clientId?: string, practiceId?: string, userId?: string) => {
    set({ loading: true, error: null });
    try {
      const response = await calendarService.getCalendarEvents(startDate, endDate, clientId, practiceId, userId);
      // La risposta API ha struttura { success: true, data: CalendarEvent[] }
      // OPPURE { success: true, data: { appointments: [], practiceDeadlines: [] } }
      const apiData = response.data || {};
      
      // Caso 1: Array diretto di eventi (già mappati)
      if (Array.isArray(apiData) && apiData.length > 0 && apiData[0].type) {
        set({
          events: apiData,
          loading: false,
        });
        return;
      }
      
      // Caso 2: Struttura con appointments e practiceDeadlines
      const dataObj = apiData.data || apiData;
      const appointments = Array.isArray(dataObj.appointments) ? dataObj.appointments.map((apt: any) => ({
        id: apt.id,
        title: apt.title,
        start: apt.startTime,
        end: apt.endTime,
        allDay: false,
        type: 'appointment' as const,
        status: apt.status,
        client: apt.client ? `${apt.client.firstName} ${apt.client.lastName}` : undefined,
        practice: apt.practice?.title,
        practiceId: apt.practiceId || apt.practice?.id,
        description: apt.description,
        location: apt.location,
        user: apt.user ? `${apt.user.firstName} ${apt.user.lastName}` : undefined,
        color: '#4F46E5',
        appointmentId: apt.id, // Mantieni anche appointmentId per compatibilità
      })) : [];
      
      const deadlines = Array.isArray(dataObj.practiceDeadlines) ? dataObj.practiceDeadlines.map((dl: any) => ({
        id: `deadline-${dl.practiceId}`,
        title: `Scadenza: ${dl.practiceTitle}`,
        start: dl.dueDate,
        end: dl.dueDate,
        allDay: true,
        type: 'practice_deadline' as const,
        status: dl.status,
        client: dl.clientName,
        practiceType: dl.practiceType?.name || dl.practiceType?.code,
        description: `Scadenza pratica ${dl.practiceTitle}`,
        color: '#DC2626',
      })) : [];
      
      // Aggiungi attività al calendario
      const activities = Array.isArray(dataObj.activities) ? dataObj.activities.map((act: any) => ({
        id: `activity-${act.id}`,
        title: act.title,
        start: act.dueDate,
        end: act.dueDate,
        allDay: false,
        type: 'activity' as const,
        status: act.status,
        priority: act.priority,
        client: act.clientName,
        practice: act.practiceTitle,
        description: act.description,
        user: act.userName,
        color: act.priority === 'URGENT' ? '#DC2626' : act.priority === 'HIGH' ? '#F97316' : '#3B82F6',
        activityId: act.id,
        practiceId: act.practiceId,
      })) : [];
      
      const finalEvents = [...appointments, ...deadlines, ...activities];
      
      set({
        events: finalEvents,
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nel caricamento degli eventi',
        loading: false,
      });
    }
  },

  setView: (view: CalendarState['view']) => {
    set({ view });
  },

  setCurrentDate: (date: Date) => {
    set({ currentDate: date });
  },

  setSelectedDate: (date: Date | null) => {
    set({ selectedDate: date });
  },

  reset: () => {
    set(initialState);
  },
}));

